import { CaretRight } from "@phosphor-icons/react";
import { GiftCardPopup } from "@shopflo/ui/gift-cards";
import { GiftCardConfigType } from "@shopflo/ui/gift-cards/types";
import { Icon } from "@shopflo/ui/icon";
import SwitchCouponDialog from "components/checkout/dialogs/SwitchCouponDialog";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { deleteRequest } from "lib/core/apiClient";
import {
  applyGiftCard as applyGiftCardToCheckout,
  getGiftCardBalance,
  rechargeGiftCardWallet,
  removeGiftCard,
} from "lib/core/apiMethods";
import useGiftCardAnalytics from "lib/hooks/useGiftCardAnalytics";
import useGiftCards from "lib/hooks/useGiftCards";
import { useLocale } from "lib/hooks/useLocale";
import { GiftCardAppliedCardType, GiftCardProviderType, GiftCardType } from "lib/types/giftCards";
import { appendParamsToItemsUrl } from "lib/utils/checkout";
import { IMAGE_CDN_URL } from "lib/utils/constants";
import { currencyFormatter } from "lib/utils/formatters";
import {
  getGiftCardCredentials,
  getGiftCardErrorMessage,
  getProviderFromVoucherPaymentMode,
} from "lib/utils/giftCards";
import { getInvalidDiscountCodes } from "lib/utils/rewards";
import { errorToast } from "lib/utils/toasters";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";
import React, { useState } from "react";
import { mutate } from "swr";

const MultiGiftCard = ({
  dividerColor,
  variant = "inline",
}: {
  dividerColor?: string;
  variant?: "inline" | "standalone";
}) => {
  const { t } = useLocale();
  const {
    state: { checkoutId, giftCards, checkoutView, isC2P, billing, checkoutModal },
    actions: { setCheckoutModal, updateCheckoutBasedOnCheckoutResponse },
  } = useCheckoutContext();
  const {
    state: { giftCardConfig },
  } = useGiftCards();
  const {
    state: { merchant, checkoutMetadata },
  } = useMerchantContext();
  const {
    actions: { mutatePayment },
  } = usePaymentContext();
  const { openPopup, closePopup, activePopup } = usePopupOrchestrator();
  const { trackGiftCardEvent } = useGiftCardAnalytics();
  const isOpen = activePopup?.key === "GIFT_CARD";
  const [isRemoving, setIsRemoving] = useState(false);
  const [unavailableProvider, setUnavailableProvider] = useState<GiftCardConfigType | null>(null);
  const [isSwitchCouponDialogOpen, setIsSwitchCouponDialogOpen] = useState(false);
  const hasTopLevelUnavailability = (giftCardConfig?.unavailabilityReasons?.length ?? 0) > 0;
  const { invalidDiscountCodeTitles, invalidDiscountCodes } = getInvalidDiscountCodes(
    (hasTopLevelUnavailability
      ? giftCardConfig?.unavailabilityReasons
      : unavailableProvider?.unavailability_reasons) ?? [],
  );

  const isUnavailabilityScope: "ALL" | null =
    (hasTopLevelUnavailability
      ? giftCardConfig.unavailabilityReasons[0]?.scope
      : unavailableProvider?.unavailability_reasons?.[0]?.scope) ?? null;
  if (
    Boolean(isC2P) ||
    checkoutView !== "PAYMENTS" ||
    !giftCardConfig?.isEnabled ||
    (!giftCardConfig?.providers?.length && !hasTopLevelUnavailability)
  )
    return <></>;

  const divider = dividerColor ? (
    <div className="h-px mx-3" style={{ backgroundColor: dividerColor }} />
  ) : null;

  /**
   * Standalone only. The coupon card's own theming, borrowed so the lone gift card still reads as
   * the same group of controls the shopper would otherwise have seen. Each falls back to the
   * class-level green when the merchant has configured nothing — an undefined style value leaves
   * the class in charge.
   */
  const discountConfig = checkoutMetadata?.uiAttributes?.discountConfig;
  const appliedCards: GiftCardAppliedCardType[] = (giftCards ?? [])
    .filter((giftCard) => Boolean(giftCard?.transaction_id))
    .map((giftCard) => ({
      transaction_id: giftCard?.transaction_id as string,
      amount: giftCard?.amount,
      provider: giftCard?.provider,
      ...(giftCard?.masked_number ? { masked_number: giftCard.masked_number } : {}),
    }));

  const validateGiftCardCode = async (provider: GiftCardProviderType, cardNumber: string, pin?: string) => {
    trackGiftCardEvent({ type: "VALIDATION_INITIATED", payload: { provider, card_number: cardNumber } });
    const validationStartedAt = Date.now();
    try {
      const response = await getGiftCardBalance(checkoutId, {
        provider,
        ...(cardNumber ? { card_number: cardNumber } : {}),
        ...(pin ? { pin } : {}),
      });
      trackGiftCardEvent({
        type: "VALIDATION_SUCCESS",
        payload: {
          provider,
          card_number: cardNumber,
          balance: response?.balance ?? 0,
          latency_ms: Date.now() - validationStartedAt,
        },
      });
      return response?.max_applicable ?? 0;
    } catch (error) {
      trackGiftCardEvent({
        type: "VALIDATION_FAILED",
        payload: { provider, card_number: cardNumber, error },
      });
      throw getGiftCardErrorMessage(error, t("something_went_wrong"));
    }
  };
  const applyGiftCard = async (card: GiftCardType) => {
    const { cardNumber, pin } = getGiftCardCredentials(card);
    try {
      const response = await applyGiftCardToCheckout(checkoutId, {
        provider: card?.provider,
        ...(cardNumber ? { card_number: cardNumber } : {}),
        ...(pin ? { pin } : {}),
        // `amount` is only accepted on a CUSTOM provider; FULL_AMOUNT takes as much as fits.
        ...(card?.amount_strategy === "CUSTOM" && card?.redeemValue > 0 ? { amount: card.redeemValue } : {}),
      });

      updateCheckoutBasedOnCheckoutResponse(response);
      trackGiftCardEvent({ type: "APPLIED", payload: { provider: card?.provider, card_number: cardNumber, response } });
      mutate(`/checkout/${checkoutId}/rewards`);
      mutatePayment(false);
      if (Number(giftCardConfig?.maxUsagePerOrder ?? 0) <= 1) {
        closeGiftCardPopup();
      }
    } catch (error) {
      throw getGiftCardErrorMessage(error, t("gift_card_error"));
    }
  };

  const deleteGiftCard = async (card: GiftCardAppliedCardType, surface: "modal" | "checkout") => {
    try {
      const response = await removeGiftCard(checkoutId, card?.transaction_id);
      updateCheckoutBasedOnCheckoutResponse(response);
      trackGiftCardEvent({ type: "REMOVED", payload: { card, surface } });
      mutate(appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`));
      mutate(`/checkout/${checkoutId}/rewards`);
      mutatePayment(false);
    } catch (error) {
      throw getGiftCardErrorMessage(error, t("gift_card_error"));
    }
  };

  const isFullyCovered = appliedCards.length > 0 && (billing?.total_payable ?? 0) <= 0;

  const singleAppliedCard = appliedCards.length === 1 ? appliedCards[0] : undefined;
  const showInlineRemove = Boolean(singleAppliedCard) && Number(giftCardConfig?.maxUsagePerOrder ?? 0) <= 1;

  const handleRemoveSingleCard = async (e: React.MouseEvent<HTMLSpanElement>) => {
    // The row itself opens the popup — removing must not do both.
    e.stopPropagation();
    if (!singleAppliedCard || isRemoving) return;

    setIsRemoving(true);
    try {
      await deleteGiftCard(singleAppliedCard, "checkout");
    } catch (error) {
      // `deleteGiftCard` has already toasted. It rethrows for the popup, which keeps the failed
      // row and records the error against it; this row has no such state to record it on.
    } finally {
      setIsRemoving(false);
    }
  };

  const rechargeWallet = async (provider: GiftCardProviderType, voucherCode: string) => {
    try {
      const response = await rechargeGiftCardWallet(checkoutId, {
        provider,
        voucher_code: voucherCode,
      });
      return response?.amount ?? 0;
    } catch (error) {
      throw getGiftCardErrorMessage(error, t("gift_card_error"));
    }
  };

  const handleRemoveAllDiscounts = async () => {
    try {
      setCheckoutModal("LOADER");
      const response = await deleteRequest(
        `/checkout/${checkoutId}/discounts`,
        { discount_codes: invalidDiscountCodes },
        "KRATOS_PRIVATE",
      );
      updateCheckoutBasedOnCheckoutResponse(response);
      const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
      await Promise.allSettled([
        mutate(DISCOUNT_PATH),
        mutatePayment(),
        mutate(`/checkout/${checkoutId}/rewards`),
      ]);
      openPopup("GIFT_CARD", {});
    } catch (error) {
      errorToast(t("failed_to_replace_coupons"));
    } finally {
      setCheckoutModal("NONE");
      setIsSwitchCouponDialogOpen(false);
      setUnavailableProvider(null);
    }
  };

  const openGiftCardPopup = () => {
    openPopup("GIFT_CARD", {});
    trackGiftCardEvent({ type: "MODAL_OPENED" });
  };

  const closeGiftCardPopup = () => {
    closePopup();
    trackGiftCardEvent({ type: "MODAL_CLOSED" });
  };

  const handleReplaceCoupons = async () => {
    if (isUnavailabilityScope === "ALL") {
      await handleRemoveAllDiscounts();
      return;
    }
    try {
      setCheckoutModal("LOADER");
      const promises = invalidDiscountCodes?.map((code: string) =>
        deleteRequest(`/checkout/v1/checkout/${checkoutId}/discounts`, { discount_code: code }),
      );
      const responses = await Promise.allSettled(promises);
      let replacedSuccessfully = true;
      let lastResponse;

      responses.forEach((response) => {
        if (response?.status !== "fulfilled") {
          replacedSuccessfully = false;
        } else {
          lastResponse = response.value;
        }
      });

      if (!replacedSuccessfully) {
        errorToast(t("failed_to_replace_coupons"));
        return;
      }

      updateCheckoutBasedOnCheckoutResponse(lastResponse);
      const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);
      await Promise.allSettled([
        mutate(DISCOUNT_PATH),
        mutatePayment(),
        mutate(`/checkout/${checkoutId}/rewards`),
      ]);
      openGiftCardPopup();
    } catch (error) {
      errorToast(t("failed_to_replace_coupons"));
    } finally {
      setCheckoutModal("NONE");
      setIsSwitchCouponDialogOpen(false);
      setUnavailableProvider(null);
    }
  };

  const giftCardRow = (
    <button
      type="button"
      className="flex justify-between w-full py-2.5 px-3 disabled:opacity-40 disabled:cursor-not-allowed"
      onClick={(e) => {
        e.stopPropagation();
        if (hasTopLevelUnavailability) {
          setIsSwitchCouponDialogOpen(true);
          return;
        }
        openGiftCardPopup();
      }}>
      <div className="text-text-xs-medium flex items-center gap-1.5">
        <img src={`${IMAGE_CDN_URL}/giftcardlogo.svg`} alt="" className="h-4 w-4" />
        <p className="self-end">{giftCardConfig?.uiModalConfig?.title ?? t("have_any_gc")}</p>
      </div>
      {showInlineRemove ? (
        <span
          onClick={handleRemoveSingleCard}
          className={`text-text-xs-medium ${
            isRemoving ? "pointer-events-none opacity-40" : "cursor-pointer"
          }`}>
          {t("remove")}
        </span>
      ) : (
        <div className="flex items-center gap-1">
          {appliedCards.length > 0 && (
            <span className="text-text-xs-medium">
              {t("x_coupons_applied", { count: appliedCards.length })}
            </span>
          )}
          <div className="flex h-3.5 w-3.5 items-center justify-center">
            <Icon
              IconComponent={CaretRight}
              size="xxxs"
              className="text-zinc-700/60"
              iconProps={{ weight: "bold" }}
            />
          </div>
        </div>
      )}
    </button>
  );

  return (
    <>
      {variant === "standalone" ? (
        <div
          id="gift-card-section"
          className="mt-3 overflow-hidden rounded-xl border border-green-dark/20 bg-green-dark/5 text-green-dark"
          style={{
            borderRadius: discountConfig?.cardRadius,
            backgroundColor: discountConfig?.footer?.backgroundColor,
          }}>
          {giftCardRow}
        </div>
      ) : (
        <>
          {divider}
          {giftCardRow}
        </>
      )}
      <GiftCardPopup
        isOpen={isOpen}
        container={document.getElementById("checkout-ui-root")}
        onClose={closeGiftCardPopup}
        coupons={giftCardConfig?.providers ?? []}
        appliedCards={appliedCards}
        maxUsage={giftCardConfig?.maxUsagePerOrder}
        formatAmount={currencyFormatter}
        isFullyCovered={isFullyCovered}
        uiModalConfig={giftCardConfig?.uiModalConfig}
        accentColor={merchant?.colorPallet?.primaryColor}
        showCloseButton={true}
        validateCode={validateGiftCardCode}
        handleApply={applyGiftCard}
        rechargeWallet={rechargeWallet}
        handleDelete={(card) => deleteGiftCard(card, "modal")}
        onProviderUnavailable={(card) => {
          setUnavailableProvider(card);
          closeGiftCardPopup();
          setIsSwitchCouponDialogOpen(true);
        }}
      />

      <GenericDialog
        isOpen={isSwitchCouponDialogOpen}
        translateAxis="y"
        dialogOverlay={true}
        customClass="overflow-scroll md:!top-auto md:absolute">
        <SwitchCouponDialog
          closePopup={() => {
            setIsSwitchCouponDialogOpen(false);
            setUnavailableProvider(null);
          }}
          applyCoupon={handleReplaceCoupons}
          appliedDiscountCode={
            unavailableProvider?.display_config?.label ??
            giftCardConfig?.uiModalConfig?.title ??
            t("have_any_gc")
          }
          invalidDiscountCodes={invalidDiscountCodeTitles ?? []}
          invalidReason={"REWARDS_NOT_COMBINABLE"}
        />
      </GenericDialog>
    </>
  );
};

export default MultiGiftCard;
